import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { dashAPI } from "../api";
import { toast } from "react-toastify";

const BaseRoute = "contactapi/api/v1/contacts";

// --- ASYNC THUNKS ---

export const fetchContacts = createAsyncThunk(
  "contacts/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(BaseRoute);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const fetchContact = createAsyncThunk(
  "contacts/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const response = await dashAPI.get(`${BaseRoute}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateContact = createAsyncThunk(
  "contacts/update",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      // Sends pure JSON as requested by ContactRequest schema
      const response = await dashAPI.patch(`${BaseRoute}/${id}`, values);
      toast.success("Contact updated successfully!");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const deleteContact = createAsyncThunk(
  "contacts/delete",
  async (id, { rejectWithValue }) => {
    try {
      await dashAPI.delete(`${BaseRoute}/${id}`);
      toast.success("Message deleted successfully!");
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// --- SLICE & REDUCER ---

const contactSlice = createSlice({
  name: "contacts",
  initialState: {
    loading: false,
    contacts: [],
    contact: {},
    error: "",
  },
  reducers: {
    clearCurrentContact: (state) => {
      state.contact = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload;
      })
      .addCase(fetchContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contact = action.payload;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.contacts.findIndex(
          (c) => c.id === action.payload.id,
        );
        if (index !== -1) state.contacts[index] = action.payload;
        if (state.contact.id === action.payload.id)
          state.contact = action.payload;
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = state.contacts.filter((c) => c.id !== action.payload);
      })
      .addMatcher(
        (action) =>
          action.type.startsWith("contacts/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = "";
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("contacts/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
          toast.error(
            action.payload || "An error occurred with the Contacts API",
          );
        },
      );
  },
});

export const { clearCurrentContact } = contactSlice.actions;
export const ContactReducer = contactSlice.reducer;
